import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Brain } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { findSnippetInText } from '@/utils/textMatching';

interface HighlightRange {
  text: string;
  absoluteStart: number;
  absoluteEnd: number;
  isFocused: boolean;
  reasoning?: string;
  relevance?: string;
  scrollTrigger?: number;
}

interface DocumentContentProps {
  documentText: string;
  highlights: HighlightRange[];
}

export default function DocumentContent({ documentText, highlights }: DocumentContentProps) {
  const highlightRef = useRef<HTMLSpanElement>(null);

  const focusedHighlight = highlights.find(h => h.isFocused);

  useEffect(() => {
    // Scroll to focused highlight after render
    const timer = setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [documentText, focusedHighlight?.absoluteStart, focusedHighlight?.text, focusedHighlight?.scrollTrigger]);

  const refinedHighlights = useMemo(() => {
    if (!documentText || !highlights.length) return [];

    return highlights.map(h => {
      const match = findSnippetInText(documentText, h.text, h.absoluteStart);
      
      if (match) {
        return { ...h, start: match.start, end: match.end };
      }

      return null;
    }).filter(Boolean) as (HighlightRange & { start: number, end: number })[];
  }, [documentText, highlights]);

  const renderContent = () => {
    if (!documentText) return null;
    if (refinedHighlights.length === 0) {
      return <p className="text-sm leading-relaxed whitespace-pre-wrap">{documentText}</p>;
    }

    const sorted = [...refinedHighlights].sort((a, b) => a.start - b.start);
    const nonOverlapping: (HighlightRange & { start: number, end: number })[] = [];
    let lastEnd = -1;

    for (const h of sorted) {
      if (h.start >= lastEnd) {
        nonOverlapping.push(h);
        lastEnd = h.end;
      } else if (h.isFocused) {
        const last = nonOverlapping[nonOverlapping.length - 1];
        if (last && !last.isFocused) {
          nonOverlapping[nonOverlapping.length - 1] = h;
          lastEnd = h.end;
        }
      }
    }

    const result: React.ReactNode[] = [];
    let currentPos = 0;

    nonOverlapping.forEach((h, i) => {
      if (h.start > currentPos) {
        result.push(documentText.substring(currentPos, h.start));
      }

      result.push(
        <TooltipProvider key={`highlight-wrapper-${i}`}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                ref={h.isFocused ? highlightRef : null}
                className={cn(
                  "px-0.5 py-0 rounded-sm font-medium transition-all duration-300 relative inline",
                  h.isFocused
                    ? "bg-yellow-300 dark:bg-yellow-700 text-black dark:text-white shadow-[0_0_0_1px_rgba(250,204,21,0.5)] z-10"
                    : "bg-yellow-100/60 dark:bg-yellow-900/40 text-inherit"
                )}
              >
                {documentText.substring(h.start, h.end)}
                {h.reasoning && (
                  <span className="inline-flex items-center justify-center ms-1 align-middle bg-gold text-white rounded-full p-0.5 shadow-sm hover:scale-110 transition-transform cursor-help">
                    <Brain size={10} />
                  </span>
                )}
              </span>
            </TooltipTrigger>
            {h.reasoning && (
              <TooltipContent className="max-w-xs p-3 bg-primary text-primary-foreground border-primary/50 rounded-xl shadow-xl">
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Brain className="h-4 w-4 text-gold" />
                    <span className="text-[10px] font-black uppercase tracking-tighter text-gold">
                      {h.relevance?.replace('_', ' ') || 'AI ANALYSIS'}
                    </span>
                  </div>
                  <p className="text-xs font-medium leading-relaxed italic opacity-90">
                    "{h.reasoning}"
                  </p>
                </div>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      );
      currentPos = h.end;
    });

    if (currentPos < documentText.length) {
      result.push(documentText.substring(currentPos));
    }

    return (
      <div className="text-sm leading-relaxed whitespace-pre-wrap">
        {result}
      </div>
    );
  };

  return renderContent();
}
